import { useEffect, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { voiceApi } from "api/voice";
import { CreateLeadModal } from "shared/app/LeadsTableView/components/CreateLeadModal";
import { useSoftphone } from "./SoftphoneProvider";
import { CallScreen } from "./CallScreen";
import { ActiveCallPill } from "./ActiveCallPill";
import { IncomingCallScreen } from "./IncomingCallScreen";
import { IncomingCallPill } from "./IncomingCallPill";
import { SoftphoneToasts } from "./SoftphoneToasts";
import { IN_CALL_LEAD_MODAL_Z_INDEX } from "./stacking";
import { isCallerUnknown } from "shared/utils/transferred-call";
import { ZERO_DRAG_OFFSET, type DragOffset } from "./useDraggableOverlay";

// The in-call Create-lead form pre-fills the caller's number; the primary
// phone field expects a bare 10-digit US number (the form re-prepends "1").
const toTenDigit = (e164: string) =>
  e164.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");

/**
 * App-wide call UI controller (CRM-811 + CRM-905). Renders the incoming-call
 * popup or the in-call card (expanded) / pill (minimized) for the current call
 * from any surface, hosts the in-call Create-lead modal, and mounts the
 * mic-permission / reconnect toasts (CRM-1073). Mounted once inside
 * SoftphoneProvider (see App.tsx).
 *
 * The drag offset (CRM-1112, CRM-1318) lives here rather than in any surface
 * so it survives collapsing to the pill and back, and answering a ring — the
 * in-call card takes over exactly where the incoming popup was dropped.
 */
export function CallOverlay() {
  const { activeCall, incomingCall, status, attachLeadToActiveCall } =
    useSoftphone();
  const [minimized, setMinimized] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  // Owned here, not by the card: the pill opens the pad while collapsed
  // (CRM-1190), and the state has to survive the card ↔ pill swap.
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState<DragOffset>(ZERO_DRAG_OFFSET);

  const callId = activeCall?.callId;
  const answeredAt = activeCall?.answeredAt;
  const incomingTwilioCall = incomingCall?.twilioCall;
  const showsCallUi = !!incomingCall || !!activeCall;

  // Each new call (or new incoming ring) opens expanded.
  useEffect(() => {
    setMinimized(false);
  }, [callId, incomingTwilioCall]);

  // A new call resets any Create-lead form or dial pad left open from the
  // previous one.
  useEffect(() => {
    setCreateLeadOpen(false);
    setKeypadOpen(false);
  }, [callId]);

  // The dragged position outlives whichever surface is showing it. Keying the
  // reset on `callId` (as CRM-1112 did) snapped the card back to the corner the
  // moment a ring was answered — `incomingCall` becomes `activeCall` in that
  // same render — so a ring dragged off the Inbox Send button landed right
  // back on it (CRM-1318). The offset is therefore only cleared once nothing
  // call-related is on screen, and the NEXT call starts from the default
  // corner. Clearing while the overlay is empty (rather than when the next
  // call appears) also means no surface ever paints a frame at a stale spot.
  useEffect(() => {
    if (!showsCallUi) setDragOffset(ZERO_DRAG_OFFSET);
  }, [showsCallUi]);

  // Tick the in-call duration from the moment the call was answered.
  useEffect(() => {
    if (status !== "in-call" || !answeredAt) {
      setElapsedSeconds(0);
      return;
    }
    const tick = () =>
      setElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - answeredAt.getTime()) / 1000))
      );
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [status, answeredAt]);

  // Attach the lead created mid-call to the call record so the recording and
  // transcript land on its activity log (CRM-905). The local state flips the
  // card's button to "Contact details" immediately either way.
  const linkLead = useMutation({
    mutationKey: ["VOICE_CALL_LEAD", "UPDATE"],
    mutationFn: ({ callId: id, leadId }: { callId: number; leadId: number }) =>
      voiceApi.linkLead(id, leadId),
    onError: () =>
      toast.error("The lead was created but couldn't be attached to this call.")
  });

  const handleLeadCreated = (lead: { id: number }) => {
    attachLeadToActiveCall(lead);
    if (callId) linkLead.mutate({ callId, leadId: lead.id });
  };

  let callUi: ReactNode = null;
  if (incomingCall)
    callUi = minimized ? (
      <IncomingCallPill
        onExpand={() => setMinimized(false)}
        dragOffset={dragOffset}
        onDragOffsetChange={setDragOffset}
      />
    ) : (
      <IncomingCallScreen
        onMinimize={() => setMinimized(true)}
        dragOffset={dragOffset}
        onDragOffsetChange={setDragOffset}
      />
    );
  else if (activeCall)
    callUi = minimized ? (
      <ActiveCallPill
        elapsedSeconds={elapsedSeconds}
        onExpand={() => setMinimized(false)}
        // The pad only fits the expanded card, so reaching for it from the pill
        // opens both — an IVR prompt won't wait for two deliberate clicks.
        onOpenKeypad={() => {
          setKeypadOpen(true);
          setMinimized(false);
        }}
        dragOffset={dragOffset}
        onDragOffsetChange={setDragOffset}
      />
    ) : (
      <CallScreen
        elapsedSeconds={elapsedSeconds}
        onMinimize={() => setMinimized(true)}
        onCreateLead={() => setCreateLeadOpen(true)}
        keypadOpen={keypadOpen}
        onKeypadOpenChange={setKeypadOpen}
        dragOffset={dragOffset}
        onDragOffsetChange={setDragOffset}
      />
    );

  return (
    <>
      {callUi}
      {activeCall ? (
        <CreateLeadModal
          open={createLeadOpen}
          onClose={() => setCreateLeadOpen(false)}
          // The overlay lives outside any leads table; the new lead shows up
          // through the call card's "Contact details" link instead.
          refetchLeads={() => {}}
          defaults={{
            // An escalated call whose caller the api could not identify carries
            // the AI agent line's own number (CRM-1393). Pre-filling it here is
            // exactly how the "hi n/a" junk leads on the park numbers were born,
            // and the next transfer from that park then resolved to one of them.
            primaryPhone: isCallerUnknown(
              activeCall.toNumber,
              activeCall.transferredFromNumber
            )
              ? undefined
              : toTenDigit(activeCall.toNumber),
            sourceName: "Incoming call"
          }}
          onCreated={handleLeadCreated}
          zIndex={IN_CALL_LEAD_MODAL_Z_INDEX}
        />
      ) : null}
      <SoftphoneToasts />
    </>
  );
}
